import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Drawer } from "../Drawer";
import { Icon } from "../ui/Icon";
import { trpc } from "../../lib/trpc";
import { PriceAgreementDrawer } from "./PriceAgreementDrawer";

interface Props {
  open: boolean;
  customerId: string | null;
  onClose: () => void;
}

const CUR_SYMBOL: Record<string, string> = { TRY: "₺", USD: "$", EUR: "€", GBP: "£" };
const miniLbl: React.CSSProperties = { fontSize: 11, color: "var(--neutral-500)", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.04em" };

function fmtMoney(n: number): string {
  return new Intl.NumberFormat("tr-TR", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n);
}
function fmtDate(d: string | Date): string {
  return new Intl.DateTimeFormat("tr-TR", { day: "2-digit", month: "2-digit", year: "numeric" }).format(new Date(d));
}

/** Müşteri detayı + o müşterinin fiyat anlaşmaları. "Yeni Anlaşma" cascade drawer açar. */
export function CustomerDetailDrawer({ open, customerId, onClose }: Props) {
  const qc = useQueryClient();
  const [addOpen, setAddOpen] = useState(false);

  const custQ = trpc.customer.get.useQuery({ id: customerId! }, { enabled: open && !!customerId });
  const agrQ = trpc.priceAgreement.listByCustomer.useQuery(
    { customerId: customerId!, includeInactive: true },
    { enabled: open && !!customerId },
  );
  const toggleMut = trpc.priceAgreement.toggle.useMutation({ onSuccess: () => qc.invalidateQueries() });

  // Satır-içi fiyat düzenleme
  const [editId, setEditId] = useState<string | null>(null);
  const [editPrice, setEditPrice] = useState("");
  const [editRate, setEditRate] = useState("");
  const [editErr, setEditErr] = useState<string | null>(null);
  const num = (s: string) => Number((s || "").replace(",", "."));
  const updateMut = trpc.priceAgreement.update.useMutation({ onSuccess: () => { qc.invalidateQueries(); setEditId(null); } });

  function startEdit(a: { id: string; unitPrice: number; exchangeRate: number | null }) {
    setEditId(a.id);
    setEditPrice(String(a.unitPrice));
    setEditRate(a.exchangeRate != null ? String(a.exchangeRate) : "");
    setEditErr(null);
    updateMut.reset();
  }
  function saveEdit(a: { id: string; currency: string }) {
    setEditErr(null);
    const p = num(editPrice);
    if (!(p > 0)) return setEditErr("Geçerli bir fiyat girin.");
    let rate: number | null | undefined;
    if (a.currency !== "TRY") {
      const r = num(editRate);
      if (!(r > 0)) return setEditErr("Döviz anlaşmasında kur (₺) zorunlu.");
      rate = r;
    }
    updateMut.mutate({ id: a.id, unitPrice: p, exchangeRate: rate });
  }

  const c = custQ.data;
  const agreements = agrQ.data ?? [];

  return (
    <>
      <Drawer
        open={open}
        onClose={onClose}
        size="xxl"
        title={c ? c.name : "Müşteri"}
        subtitle={c ? `Kod: ${c.code}` : undefined}
        footer={<button type="button" className="btn-md ghost" onClick={onClose}>Kapat</button>}
      >
        {!c ? (
          <div style={{ padding: 24, color: "var(--neutral-500)" }}>Yükleniyor…</div>
        ) : (
          <>
            <div className="form-row" style={{ gridTemplateColumns: "1fr 1fr 1fr", marginBottom: 4 }}>
              <div><div style={miniLbl}>Vergi No</div><div className="mono" style={{ fontSize: 13 }}>{c.taxNumber || "—"}</div></div>
              <div><div style={miniLbl}>Şehir / Ülke</div><div style={{ fontSize: 13 }}>{c.city ? `${c.city}${c.country ? " · " + c.country : ""}` : "—"}</div></div>
              <div><div style={miniLbl}>İletişim</div><div style={{ fontSize: 12.5 }}>{c.email || c.phone || "—"}</div></div>
            </div>

            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginTop: 16, marginBottom: 10, borderTop: "1px solid var(--neutral-100)", paddingTop: 14 }}>
              <span style={{ fontSize: 13, fontWeight: 600, color: "var(--neutral-800)" }}>Fiyat Anlaşmaları ({agreements.length})</span>
              <button type="button" className="btn-md primary" style={{ height: 30, fontSize: 12.5 }} onClick={() => setAddOpen(true)}><Icon name="plus" size={13} />Yeni Anlaşma</button>
            </div>

            {agrQ.isLoading ? (
              <div className="d-empty"><span className="m">Yükleniyor…</span></div>
            ) : agreements.length === 0 ? (
              <div className="d-empty"><Icon name="inbox" size={24} /><span className="m">Bu müşteriyle henüz fiyat anlaşması yok.</span></div>
            ) : (
              <div style={{ border: "1px solid var(--neutral-200)", borderRadius: "var(--radius-md)", overflow: "hidden" }}>
                <table className="dt2">
                  <thead>
                    <tr>
                      <th>Ürün</th>
                      <th className="num" style={{ width: 150 }}>Birim Fiyat</th>
                      <th style={{ width: 100 }}>Kur (₺)</th>
                      <th style={{ width: 96 }}>Tarih</th>
                      <th style={{ width: 84 }}>Durum</th>
                      <th style={{ width: 80 }}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {agreements.map((a) => {
                      const editing = editId === a.id;
                      const sym = CUR_SYMBOL[a.currency] ?? a.currency;
                      return (
                      <tr key={a.id} style={{ opacity: a.isActive ? 1 : 0.5 }}>
                        <td>{a.itemName}<span className="sec mono" style={{ fontSize: 10.5 }}>{a.itemCode}</span></td>
                        <td className="cell-num">
                          {editing ? (
                            <span style={{ display: "inline-flex", alignItems: "center", gap: 4, justifyContent: "flex-end" }}>
                              <input className="form-input" type="text" value={editPrice} onChange={(e) => setEditPrice(e.target.value.replace(/[^\d.,]/g, ""))} inputMode="decimal" aria-label="Birim fiyat" style={{ width: 92, height: 30, fontSize: 12.5, textAlign: "right" }} autoFocus />
                              <span className="unit">{sym}</span>
                            </span>
                          ) : (
                            <>{fmtMoney(a.unitPrice)}<span className="unit">{sym}</span></>
                          )}
                        </td>
                        <td>
                          {editing && a.currency !== "TRY" ? (
                            <input className="form-input" type="text" value={editRate} onChange={(e) => setEditRate(e.target.value.replace(/[^\d.,]/g, ""))} inputMode="decimal" aria-label="Kur (TL karşılığı)" style={{ width: 78, height: 30, fontSize: 12.5 }} />
                          ) : (
                            <span className="mono" style={{ fontSize: 11.5, color: "var(--neutral-600)" }}>{a.exchangeRate != null ? fmtMoney(a.exchangeRate) : "—"}</span>
                          )}
                        </td>
                        <td><span className="mono" style={{ fontSize: 11.5, color: "var(--neutral-600)" }}>{fmtDate(a.agreedAt)}</span></td>
                        <td>
                          {a.isLatest ? <span className="badge-pill b-success">Güncel</span> : a.isActive ? <span className="badge-pill">Geçmiş</span> : <span className="badge-pill">Pasif</span>}
                        </td>
                        <td>
                          <div className="row-acts">
                            {editing ? (
                              <>
                                <button type="button" className="iact success" aria-label="Kaydet" title="Kaydet" onClick={() => saveEdit(a)} disabled={updateMut.isPending}><Icon name="check" size={14} /></button>
                                <button type="button" className="iact" aria-label="Vazgeç" title="Vazgeç" onClick={() => setEditId(null)} disabled={updateMut.isPending}><Icon name="x" size={14} /></button>
                              </>
                            ) : (
                              <>
                                <button type="button" className="iact info" aria-label="Fiyatı düzenle" title="Fiyatı düzenle" onClick={() => startEdit(a)}><Icon name="edit" size={14} /></button>
                                <button type="button" className={a.isActive ? "iact danger" : "iact success"} aria-label={a.isActive ? "Pasifleştir" : "Aktifleştir"} title={a.isActive ? "Pasifleştir" : "Aktifleştir"} onClick={() => toggleMut.mutate({ id: a.id, isActive: !a.isActive })} disabled={toggleMut.isPending}><Icon name={a.isActive ? "archive" : "check"} size={14} /></button>
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                      );
                    })}
                  </tbody>
                </table>
                {editId && (editErr || updateMut.error) ? (
                  <div style={{ fontSize: 11.5, color: "var(--danger)", padding: "6px 10px", borderTop: "1px solid var(--neutral-100)" }} role="alert">{editErr ?? updateMut.error?.message}</div>
                ) : null}
              </div>
            )}
          </>
        )}
      </Drawer>

      {customerId ? (
        <PriceAgreementDrawer
          open={addOpen}
          customerId={customerId}
          customerName={c?.name}
          onClose={() => setAddOpen(false)}
          onCreated={() => agrQ.refetch()}
        />
      ) : null}
    </>
  );
}
